import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';
import { User } from '../../../../domain';
import { ClassroomStatusSelect, InputAutoWidth, TooltipDiv } from '../../../../lib/components';
import { concat_ws } from '../../../../lib/helpers';
import { ClassroomRole } from '../../../classroom/models/classroom-models';
import { AdminClassroomSceneStore } from '../AdminClassroomSceneStore';
import ReactInputMask from 'react-input-mask';

const datePlaceholder = 'yyyy-mm-dd';

function userTooltipContent(user: User): React.ReactNode {
  return (
    <div>
      <div>Username: {user.Username}</div>
      <div>First Name: {user.FirstName}</div>
      <div>Last Name: {user.LastName}</div>
      <div>Email: {user.Email}</div>
    </div>);
}

const font = 'normal 400 14px Roboto';

function renderUserControl(store: AdminClassroomSceneStore, role: ClassroomRole): React.ReactNode {
  const userSearchStore = role === 'Student'
    ? store.studentSearch
    : store.teacherSearch;
  const user = userSearchStore.selectUser;
  const userFilter = userSearchStore.userFilter;

  if (user === null)
    return (
      <div className="form-group user-search-string">
        <label>{role}</label>
        <InputAutoWidth type="text" className="form-control"
          font={font}
          value={userFilter.value}
          onChange={userFilter.onChangeHandler} />
      </div>);

  const clearUser = () => {
    if (role === 'Teacher')
      store.teacherSearch.setSelectedUser(null);
    if (role === 'Student')
      store.studentSearch.setSelectedUser(null);
  };

  return (
    <div className="form-group">
      <label>{role}</label>
      <div className="row no-gutters">
        <div className="col">
          <TooltipDiv className="text-box"
            tooltipText={userTooltipContent(user)}>
            {concat_ws([user.FirstName, user.LastName])}
          </TooltipDiv>
        </div>
        <div className="col-auto">
          <button type="button" className="btn btn-outline-primary"
            onClick={clearUser}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>
    </div>);
}

@observer
export class ClassroomSearchControls extends React.Component<{
  store: AdminClassroomSceneStore
}> {
  render() {
    const store = this.props.store;
    const search = this.props.store.classroomSearch;

    const isPending = search.resultState?.isPending === true;
    const isResolved = search.resultState?.isResolved === true;

    const startDateFilterClassName = classNames(
      search.startDateFilter.formGroupClassName,
      'start-date-filter');

    const endDateFilterClassName = classNames(
      search.startDateFilter.formGroupClassName,
      'start-date-filter');

    return (
      <div className="row align-items-end">
        <div className="col-auto">
          {renderUserControl(store, 'Student')}
        </div>

        <div className="col-auto">
          {renderUserControl(store, 'Teacher')}
        </div>

        <div className="col-auto">
          <div className={startDateFilterClassName}>
            <label>Start date</label>
            <ReactInputMask className="form-control date"
              mask="9999-99-99"
              value={search.startDateFilter.value}
              onChange={search.startDateFilter.onChangeHandler}
              placeholder={datePlaceholder} />
          </div>
        </div>

        <div className="col-auto">
          <div className={endDateFilterClassName}>
            <label>End date</label>
            <ReactInputMask className="form-control date"
              mask="9999-99-99"
              value={search.endDateFilter.value}
              onChange={search.endDateFilter.onChangeHandler}
              placeholder={datePlaceholder} />
          </div>
        </div>

        <div className="col-auto">
          <div className="form-group classroom-status-filter">
            <label>Status</label>
            <ClassroomStatusSelect value={search.statusFilter.value}
              onChange={search.statusFilter.setValue} />
          </div>
        </div>

        <div className="col-auto ml-auto">
          <div className="btn-group">
            <button type="button" className="btn btn-primary"
              disabled={isPending}
              onClick={search.searchClassrooms}>
              Search
            </button>
            <button type="button" className="btn btn-primary"
              disabled={!isResolved}
              onClick={search.toggleExpand}>
              <i className={!isResolved || search.expanded ? 'fas fa-minus' : 'fas fa-plus'} />
            </button>
          </div>
        </div>
      </div>);
  }
}
